/**
 * User-related React Query hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { identityServiceClient } from '../client'
import {
  UserAbilitiesRequest,
  UserAbilitiesResponse,
  UserCreate,
  UserRegisterRequest,
  UsersQueryParams,
  UserUpdate,
  UserProfileResponse,
  UserProfileUpdateRequest,
  UserProfileUpdateResponse,
} from '../'
import { QueryOptions } from '../../../lib/utils'

// Query hooks for users
export const useUsers = (params?: UsersQueryParams) => {
  return useQuery({
    queryKey: ['users', params],
    queryFn: async () => {
      const response = await identityServiceClient.GET('/users', {
        params: { query: params },
      })
      if (response.error) throw response.error
      return response.data
    },
  })
}

export const useUser = (userId: string) => {
  return useQuery({
    queryKey: ['user', userId],
    queryFn: async () => {
      const response = await identityServiceClient.GET('/users/{id}', {
        params: { path: { id: userId } },
      })
      if (response.error) throw response.error
      return response.data
    },
    enabled: !!userId,
  })
}

export const useCurrentUser = (options?: QueryOptions<UserProfileResponse, Error>) => {
  return useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const response = await identityServiceClient.GET('/users/profile')
      if (response.error) throw response.error
      return response.data
    },
    ...options,
  })
}

export const useUserAbilities = (
  params: UserAbilitiesRequest,
  options?: QueryOptions<UserAbilitiesResponse, Error>
) => {
  return useQuery({
    queryKey: ['user-abilities', params],
    queryFn: async () => {
      const response = await identityServiceClient.GET('/users/me/abilities', {
        params: { query: params },
      })
      if (response.error) throw response.error
      return response.data
    },
    ...options,
  })
}

// Mutation hooks for users
export const useCreateUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userData: UserCreate) => {
      const response = await identityServiceClient.POST('/users', {
        body: userData,
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export const useUpdateUser = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (userData: UserUpdate) => {
      const response = await identityServiceClient.PUT('/users/{id}', {
        params: { path: { id: userId } },
        body: userData,
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['user', userId] })
    },
  })
}

export const useDeleteUser = (userId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await identityServiceClient.DELETE('/users/{id}', {
        params: { path: { id: userId } },
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.removeQueries({ queryKey: ['user', userId] })
    },
  })
}

export const useRegisterUser = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: UserRegisterRequest) => {
      const response = await identityServiceClient.POST('/users/register', {
        body: request,
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: UserProfileUpdateRequest & { organizationId?: string }) => {
      const { organizationId, ...body } = request
      const response = await identityServiceClient.POST('/users/profile', {
        params: { query: organizationId ? { organizationId } : {} },
        body,
      })
      if (response.error) throw response.error
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
    },
  })
}
